import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope, StudentScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface MentalMinuteStudentResponse {
  mental_minute_details: {
    aggregate: {
      all: MentalMinuteStudentAggregateData
      division: MentalMinuteStudentAggregateData
      multiplication: MentalMinuteStudentAggregateData
      addition: MentalMinuteStudentAggregateData
      subtraction: MentalMinuteStudentAggregateData
    }
    summary: {
      division: SummaryData
      multiplication: SummaryData
      addition: SummaryData
      subtraction: SummaryData
    }
    students: {
      student_id: number
      lessons: MentalMinuteStudentLessonItem[]
    }[]
  }
}

export interface MentalMinuteStudentAggregateData {
  scores: {
    average_improvement: number
    average_first_score: number
    average_best_score: number
  }
  attempts: {
    total: number
    average: number
  }
}

interface SummaryData {
  correct_answers: number
  improvement: number
  sprints_mastered: number
}

export interface MentalMinuteStudentLessonItem {
  category: string
  lesson: number
  first_score: number
  best_score: number
  improvement: Nullable<number>
  stars: 0 | 1 | 2 | 3
  attempts: number
  last_attempted_at: number
  lesson_mastered: boolean
}

export interface MentalMinuteStudentData {
  summary: {
    division: SummaryData
    multiplication: SummaryData
    addition: SummaryData
    subtraction: SummaryData
  }
  aggregate: {
    all: MentalMinuteStudentAggregateData
    division: MentalMinuteStudentAggregateData
    multiplication: MentalMinuteStudentAggregateData
    addition: MentalMinuteStudentAggregateData
    subtraction: MentalMinuteStudentAggregateData
  }
  students: {
    student_id: number
    lessons: MentalMinuteStudentLessonItem[]
  }[]
}

export async function mentalMinuteStudent(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: SchoolScope | StudentsScope | StudentScope,
  period: NormalizedInterval,
): Promise<Nullable<MentalMinuteStudentData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('mental_minute_details', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<MentalMinuteStudentResponse>(url, query)
  if (!data) return null

  const {
    mental_minute_details: { summary, aggregate, students },
  } = data
  if (isEmpty(summary) || isEmpty(aggregate) || isEmpty(students)) return null

  return { summary, aggregate, students }
}
